/**
 * Money — invariant I1.
 *
 * Money is ALWAYS `{ amountMinor: <integer>, currency: <ISO 4217 code> }`.
 * `amountMinor` is the amount in the currency's smallest unit (fils, cents, ...).
 * There is no floating point anywhere in this file's arithmetic paths, and no other
 * file in the codebase is allowed to do arithmetic on monetary values.
 *
 * Percentages are expressed in BASIS POINTS (bps) as integers: 100 bps = 1%,
 * 10000 bps = 100%. This keeps rate math exact.
 */

/**
 * Minor-unit exponent per currency. Anything not listed defaults to 2.
 *
 * This table is the **fallback**, not the register. Currencies are rows in the database
 * (`modules/currencies`), created and edited without a deploy, and each one carries its
 * own `decimals` — so the process primes this map from that list at boot
 * (`registerCurrencies`) and a currency somebody added this morning parses with its own
 * precision instead of silently falling back to two places. The literals below are what
 * keeps `money.js` usable on its own: a unit test, a script, or the client before its
 * first API call.
 */
const CURRENCY_DECIMALS = {
  USD: 2,
  SAR: 2,
  QAR: 2,
  EGP: 2,
  AED: 2,
  EUR: 2,
  GBP: 2,
  TRY: 2,
  MAD: 2,
  SYP: 2,
  JOD: 3,
  KWD: 3,
  BHD: 3,
  OMR: 3,
  TND: 3,
  LYD: 3,
  IQD: 3,
  JPY: 0,
  KRW: 0,
  VND: 0,
  CLP: 0,
};

export const decimalsFor = (currency) => CURRENCY_DECIMALS[String(currency).toUpperCase()] ?? 2;

/**
 * Teach this module about a currency that was configured rather than hard-coded.
 *
 * Called once at boot on the server and once per `/currencies` response on the client.
 * Registering is idempotent and last-write-wins, which is correct: the database row is
 * the authority and the literal table above is only there for a cold start.
 *
 * A currency whose `decimals` is not an integer in 0–3 is ignored rather than accepted —
 * a bad row must not be able to re-scale every amount already parsed in this process.
 */
export const registerCurrency = (code, decimals) => {
  if (typeof code !== 'string' || !/^[A-Za-z]{3}$/.test(code)) return false;
  if (!Number.isInteger(decimals) || decimals < 0 || decimals > 3) return false;
  CURRENCY_DECIMALS[code.toUpperCase()] = decimals;
  return true;
};

/** @param {{code:string, decimals:number}[]} list */
export const registerCurrencies = (list = []) =>
  list.filter((c) => registerCurrency(c?.code, c?.decimals)).length;

export const BPS_SCALE = 10000;

// ---------------------------------------------------------------------------
// Construction & validation
// ---------------------------------------------------------------------------

export class MoneyError extends Error {
  constructor(message) {
    super(message);
    this.name = 'MoneyError';
  }
}

const assertInt = (n, label) => {
  if (typeof n !== 'number' || !Number.isFinite(n) || !Number.isInteger(n)) {
    throw new MoneyError(`${label} must be an integer, received ${JSON.stringify(n)}`);
  }
  if (!Number.isSafeInteger(n)) {
    throw new MoneyError(`${label} exceeds MAX_SAFE_INTEGER — amount is out of range`);
  }
  return n;
};

const assertCurrency = (c) => {
  if (typeof c !== 'string' || !/^[A-Z]{3}$/.test(c)) {
    throw new MoneyError(`currency must be a 3-letter ISO code, received ${JSON.stringify(c)}`);
  }
  return c;
};

/** Build a Money value. */
export const money = (amountMinor, currency) => ({
  amountMinor: assertInt(amountMinor, 'amountMinor'),
  currency: assertCurrency(String(currency).toUpperCase()),
});

export const zero = (currency) => money(0, currency);

export const isMoney = (v) =>
  !!v && typeof v === 'object' && Number.isInteger(v.amountMinor) && typeof v.currency === 'string';

const assertSameCurrency = (a, b) => {
  if (a.currency !== b.currency) {
    throw new MoneyError(`currency mismatch: ${a.currency} vs ${b.currency}`);
  }
};

// ---------------------------------------------------------------------------
// Conversion to/from human decimal strings
// ---------------------------------------------------------------------------

/**
 * Parse a user-entered decimal string/number into minor units.
 * Accepts "1,234.56", "1234.56", " -12.5 ", 1234.56, Arabic-Indic digits.
 * Rejects more decimal places than the currency allows.
 */
export const fromMajor = (input, currency) => {
  const cur = assertCurrency(String(currency).toUpperCase());
  const dp = decimalsFor(cur);

  let str = String(input ?? '').trim();
  // Normalise Arabic-Indic digits and Arabic decimal separator.
  str = str
    .replace(/[٠-٩]/g, (d) => String(d.charCodeAt(0) - 0x0660))
    .replace(/[۰-۹]/g, (d) => String(d.charCodeAt(0) - 0x06f0))
    .replace(/٫/g, '.')
    // Strip grouping separators and every space that turns up in pasted amounts:
    // ASCII comma, Arabic thousands separator U+066C, no-break space U+00A0 and
    // narrow no-break space U+202F (both common in Arabic and French formatting).
    .replace(/[\s,\u066C\u00A0\u202F]/g, '');

  if (str === '' || str === '-' || str === '+') return money(0, cur);

  const m = /^([+-]?)(\d*)(?:\.(\d*))?$/.exec(str);
  if (!m) throw new MoneyError(`cannot parse "${input}" as an amount`);

  const sign = m[1] === '-' ? -1 : 1;
  const whole = m[2] || '0';
  const frac = m[3] || '';

  if (frac.length > dp) {
    throw new MoneyError(`${cur} allows at most ${dp} decimal place(s), received "${input}"`);
  }

  const padded = (frac + '0'.repeat(dp)).slice(0, dp);
  const minor = Number(whole) * 10 ** dp + (dp > 0 ? Number(padded) : 0);
  return money(sign * minor, cur);
};

/** Exact decimal string (no grouping) — safe for exports and APIs. */
export const toMajorString = (m) => {
  const dp = decimalsFor(m.currency);
  const neg = m.amountMinor < 0;
  const abs = String(Math.abs(m.amountMinor)).padStart(dp + 1, '0');
  const whole = abs.slice(0, abs.length - dp) || '0';
  const frac = dp > 0 ? `.${abs.slice(abs.length - dp)}` : '';
  return `${neg ? '-' : ''}${whole}${frac}`;
};

/** Lossy — display and charting only. Never feed this back into arithmetic. */
export const toMajorNumber = (m) => Number(toMajorString(m));

// ---------------------------------------------------------------------------
// Arithmetic
// ---------------------------------------------------------------------------

export const add = (a, b) => {
  assertSameCurrency(a, b);
  return money(a.amountMinor + b.amountMinor, a.currency);
};

export const sub = (a, b) => {
  assertSameCurrency(a, b);
  return money(a.amountMinor - b.amountMinor, a.currency);
};

export const negate = (a) => money(-a.amountMinor, a.currency);

export const abs = (a) => money(Math.abs(a.amountMinor), a.currency);

/** Multiply by an integer count (e.g. qty). */
export const mulInt = (a, count) => money(a.amountMinor * assertInt(count, 'count'), a.currency);

export const sum = (list, currency) => {
  if (!list.length) {
    if (!currency) throw new MoneyError('sum of an empty list requires an explicit currency');
    return zero(currency);
  }
  return list.reduce((acc, m) => add(acc, m), zero(currency || list[0].currency));
};

export const isZero = (a) => a.amountMinor === 0;
export const isNegative = (a) => a.amountMinor < 0;
export const isPositive = (a) => a.amountMinor > 0;

export const compare = (a, b) => {
  assertSameCurrency(a, b);
  return a.amountMinor === b.amountMinor ? 0 : a.amountMinor < b.amountMinor ? -1 : 1;
};

export const eq = (a, b) => compare(a, b) === 0;
export const gt = (a, b) => compare(a, b) > 0;
export const gte = (a, b) => compare(a, b) >= 0;
export const lt = (a, b) => compare(a, b) < 0;
export const lte = (a, b) => compare(a, b) <= 0;
export const min = (a, b) => (lte(a, b) ? a : b);
export const max = (a, b) => (gte(a, b) ? a : b);

// ---------------------------------------------------------------------------
// Rounding & rates
// ---------------------------------------------------------------------------

export const ROUNDING = {
  HALF_UP: 'half_up', // 0.5 away from zero — the accounting default
  HALF_EVEN: 'half_even', // banker's rounding
  DOWN: 'down', // truncate toward zero
  UP: 'up', // away from zero
};

/** Round a rational numerator/denominator to an integer. All inputs integers. */
const roundQuotient = (numerator, denominator, mode = ROUNDING.HALF_UP) => {
  if (denominator === 0) throw new MoneyError('division by zero');
  const negative = numerator < 0 !== denominator < 0;
  const n = Math.abs(numerator);
  const d = Math.abs(denominator);
  const q = Math.floor(n / d);
  const r = n - q * d;

  let result = q;
  if (r !== 0) {
    switch (mode) {
      case ROUNDING.DOWN:
        break;
      case ROUNDING.UP:
        result = q + 1;
        break;
      case ROUNDING.HALF_EVEN:
        if (r * 2 > d || (r * 2 === d && q % 2 === 1)) result = q + 1;
        break;
      case ROUNDING.HALF_UP:
      default:
        if (r * 2 >= d) result = q + 1;
        break;
    }
  }
  return negative ? -result : result;
};

/** Apply a basis-point rate: `applyBps(money(10000,'USD'), 250)` → 2.50% → 250 minor. */
export const applyBps = (m, bps, mode = ROUNDING.HALF_UP) => {
  assertInt(bps, 'bps');
  return money(roundQuotient(m.amountMinor * bps, BPS_SCALE, mode), m.currency);
};

/**
 * The inverse of `applyBps`: what share of `whole` is `part`, in basis points.
 *
 * A **ratio, not an amount** — which is why it returns a plain integer rather than Money.
 * It exists so that "58% of this budget is used" and "the commission was 12% of the deal"
 * are computed the same way in every report, instead of each one dividing two
 * `amountMinor` fields by hand and rounding however it feels (**I1**).
 *
 * A zero `whole` gives 0, not `Infinity`: an unallocated budget is 0% used, and a report
 * that renders `Infinity%` is a bug report waiting to be filed.
 */
export const bpsOf = (part, whole, mode = ROUNDING.HALF_UP) => {
  assertSameCurrency(part, whole);
  if (whole.amountMinor === 0) return 0;
  return roundQuotient(part.amountMinor * BPS_SCALE, whole.amountMinor, mode);
};

/** Percent helper for readability: `percent(2.5)` → 250 bps. */
export const percentToBps = (pct) => {
  const n = Math.round(Number(pct) * 100);
  if (!Number.isFinite(n)) throw new MoneyError(`invalid percent ${pct}`);
  return n;
};

export const bpsToPercent = (bps) => bps / 100;

/**
 * **A rate, as somebody reads it aloud** — `2.5`, `5`, `12.75`. The `%` belongs to the
 * caller, because most of them interpolate it into a translated sentence that already
 * carries one.
 *
 * Trailing zeros are trimmed here, and that is a **different rule from `format()`**, on
 * purpose. Money keeps its exponent all-or-nothing because half a dollar written `.5`
 * reads as a typo and a ragged column stops lining up under `tabular-nums`. Neither is
 * true of a rate: `2.5%` is how a percentage is written everywhere, `2.50%` is the odd
 * one, and rates appear in badges and prose rather than in a column of figures.
 *
 * Two decimals is the ceiling because that is all a basis point can express — `250` is
 * exactly 2.5% and nothing finer exists to print.
 */
export const formatBps = (bps, { locale = 'en-US' } = {}) =>
  new Intl.NumberFormat(locale, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
    useGrouping: true,
  }).format(bpsToPercent(Number(bps) || 0));

/** qty is a quantity in thousandths (so 1.5 units → 1500) to stay integral. */
export const QTY_SCALE = 1000;

/**
 * **A quantity, as somebody would write it** — `1`, `1.5`, `2.25`.
 *
 * Same reasoning as `formatBps`: three fixed decimals turned every ordinary “one of them”
 * into `1.000`, which reads as a measurement taken to the milligram rather than a count.
 * The scale exists so a third of a unit stays exact in storage, not so every invoice line
 * has to display it.
 */
export const formatQty = (qtyMilli, { locale = 'en-US' } = {}) =>
  new Intl.NumberFormat(locale, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 3,
    useGrouping: true,
  }).format((Number(qtyMilli) || 0) / QTY_SCALE);

/** unitPrice × qty(milli-units), rounded. */
export const lineTotal = (unitPrice, qtyMilli, mode = ROUNDING.HALF_UP) =>
  money(
    roundQuotient(unitPrice.amountMinor * assertInt(qtyMilli, 'qtyMilli'), QTY_SCALE, mode),
    unitPrice.currency,
  );

/** FX conversion. rate is in micro-units (1 unit of `from` = rateMicro/1e6 units of `to`). */
export const FX_SCALE = 1_000_000;

export const convert = (m, toCurrency, rateMicro, mode = ROUNDING.HALF_UP) => {
  const to = assertCurrency(String(toCurrency).toUpperCase());
  assertInt(rateMicro, 'rateMicro');
  const fromDp = decimalsFor(m.currency);
  const toDp = decimalsFor(to);
  // Rescale minor units when the two currencies have different exponents.
  const scaleNum = 10 ** Math.max(0, toDp - fromDp);
  const scaleDen = 10 ** Math.max(0, fromDp - toDp);
  return money(roundQuotient(m.amountMinor * rateMicro * scaleNum, FX_SCALE * scaleDen, mode), to);
};

// ---------------------------------------------------------------------------
// Allocation — split without losing or inventing a single minor unit
// ---------------------------------------------------------------------------

/**
 * Split `m` across the given basis-point weights so the parts sum EXACTLY back to `m`.
 * Remainder minor units are handed out largest-remainder-first, which is the standard
 * accounting allocation rule. Used by commission splits and profit distribution.
 *
 * allocateByBps(money(10000,'USD'), [3333, 3333, 3334]) → 3333, 3333, 3334
 */
export const allocateByBps = (m, weightsBps) => {
  if (!weightsBps.length) throw new MoneyError('allocation needs at least one weight');
  const totalWeight = weightsBps.reduce((a, b) => a + assertInt(b, 'weight'), 0);
  if (totalWeight <= 0) throw new MoneyError('allocation weights must sum to a positive number');

  const sign = m.amountMinor < 0 ? -1 : 1;
  const total = Math.abs(m.amountMinor);

  const floors = weightsBps.map((w) => Math.floor((total * w) / totalWeight));
  const remainders = weightsBps.map((w, i) => ({ i, rem: total * w - floors[i] * totalWeight }));
  let left = total - floors.reduce((a, b) => a + b, 0);

  remainders.sort((a, b) => b.rem - a.rem || a.i - b.i);
  for (let k = 0; k < remainders.length && left > 0; k += 1, left -= 1) {
    floors[remainders[k].i] += 1;
  }

  return floors.map((v) => money(sign * v, m.currency));
};

/** Equal split with the remainder pushed to the earliest parts. */
export const allocateEvenly = (m, parts) =>
  allocateByBps(
    m,
    Array.from({ length: assertInt(parts, 'parts') }, () => 1),
  );

/**
 * `n` equal shares of 100%, in basis points, summing to exactly `BPS_SCALE`.
 *
 * Three ways is `[3334, 3333, 3333]`. The two basis points nobody can divide have to land
 * somewhere, and dropping them would leave a split table summing to 9999 — which the
 * commission engine refuses outright, correctly.
 *
 * **Not interchangeable with `allocateEvenly`,** and that is the reason this exists rather
 * than each caller improvising. Allocating 250.00 three ways by the weights `[1,1,1]` gives
 * 83.34 / 83.33 / 83.33; by the weights `[3334,3333,3333]` it gives 83.35 / 83.33 / 83.32.
 * Both are defensible and they are one minor unit apart, so a screen that previews a split
 * with one rule and a service that posts it with the other disagree on exactly the kind of
 * figure nobody re-checks. Both sides call this.
 */
export const evenBps = (n) => {
  const parts = assertInt(n, 'parts');
  if (parts <= 0) throw new MoneyError('an even split needs at least one part');
  const share = Math.floor(BPS_SCALE / parts);
  let left = BPS_SCALE - share * parts;
  return Array.from({ length: parts }, () => {
    const extra = left > 0 ? 1 : 0;
    left -= extra;
    return share + extra;
  });
};

// ---------------------------------------------------------------------------
// Formatting (display only)
// ---------------------------------------------------------------------------

/**
 * @param {{amountMinor:number,currency:string}} m
 * @param {{locale?:string, showCurrency?:boolean, parenthesesForNegative?:boolean}} [opts]
 */
export const format = (m, opts = {}) => {
  const { locale = 'en-US', showCurrency = true, parenthesesForNegative = true } = opts;
  const dp = decimalsFor(m.currency);
  const negative = m.amountMinor < 0;

  /*
   * **A whole amount prints whole.** 1,500.00 is three characters of noise on every row of
   * every register, and the group reads its figures in whole dinars and whole dollars far
   * more often than not.
   *
   * It is all or nothing per amount, never a trim of trailing zeros: 1,500.50 keeps both
   * places rather than becoming 1,500.5. Half a dollar written as `.5` reads as a typo, and
   * a column where some cells carry one decimal and others two stops lining up under
   * `tabular-nums` — which is the whole reason the money cell uses it.
   *
   * The exponent is still the currency's own (`decimalsFor`), so a fils on a dinar amount
   * survives at three places exactly as it did before.
   */
  const whole = Math.abs(m.amountMinor) % 10 ** dp === 0;
  const body = new Intl.NumberFormat(locale, {
    minimumFractionDigits: whole ? 0 : dp,
    maximumFractionDigits: whole ? 0 : dp,
    useGrouping: true,
  }).format(Math.abs(toMajorNumber(m)));

  const withSign = negative ? (parenthesesForNegative ? `(${body})` : `-${body}`) : body;

  return showCurrency ? `${withSign} ${m.currency}` : withSign;
};

// ---------------------------------------------------------------------------
// Debit / credit helpers used by the posting engine
// ---------------------------------------------------------------------------

/** A journal side: exactly one of debit/credit is non-zero. */
export const debitLine = (amount) => ({ debit: amount, credit: zero(amount.currency) });
export const creditLine = (amount) => ({ debit: zero(amount.currency), credit: amount });

/** Invariant I2 check. Returns { balanced, totalDebit, totalCredit, difference }. */
export const checkBalanced = (lines, currency) => {
  const totalDebit = sum(
    lines.map((l) => l.debit),
    currency,
  );
  const totalCredit = sum(
    lines.map((l) => l.credit),
    currency,
  );
  return {
    balanced: totalDebit.amountMinor === totalCredit.amountMinor,
    totalDebit,
    totalCredit,
    difference: sub(totalDebit, totalCredit),
  };
};

export default {
  money,
  zero,
  isMoney,
  decimalsFor,
  registerCurrency,
  registerCurrencies,
  fromMajor,
  toMajorString,
  toMajorNumber,
  add,
  sub,
  negate,
  abs,
  mulInt,
  sum,
  isZero,
  isNegative,
  isPositive,
  compare,
  eq,
  gt,
  gte,
  lt,
  lte,
  min,
  max,
  applyBps,
  percentToBps,
  bpsToPercent,
  lineTotal,
  convert,
  allocateByBps,
  allocateEvenly,
  evenBps,
  format,
  debitLine,
  creditLine,
  checkBalanced,
  ROUNDING,
  BPS_SCALE,
  QTY_SCALE,
  FX_SCALE,
  MoneyError,
};
